import {Component, Input, OnChanges} from '@angular/core';
import {PerformanceComparisonMetric, PerformanceMapResponse, PerformanceMapType} from '../../../../components/my-brand-screen/types';
import {NoWidgetDataComponent} from '../../../../components/my-brand-screen/no-widget-data/no-widget-data.component';
import {LoaderComponent} from '../../loader/loader.component';
import {HighchartsChartModule} from 'highcharts-angular';
import {NgIf} from '@angular/common';
import Highcharts from 'highcharts/highmaps';
import worldMap from '@highcharts/map-collection/custom/world.geo.json';
import usaMap from '@highcharts/map-collection/countries/us/us-all.geo.json';
import {SelectItem} from '../../select-menu/select-menu.component';
import {ShortDisplayValuePipe} from '../../../../pipes/short-display-value.pipe';

@Component({
  selector: 'app-performance-map',
  imports: [
    NgIf,
    HighchartsChartModule,
    NoWidgetDataComponent,
    LoaderComponent,
  ],
  providers: [
    ShortDisplayValuePipe
  ],
  templateUrl: './performance-map.component.html',
  styleUrl: './performance-map.component.scss',
  standalone: true
})
export class PerformanceMapComponent implements OnChanges {
  @Input() type?: PerformanceMapType;
  @Input() data?: PerformanceMapResponse;
  @Input() metric?: SelectItem<any>;
  @Input() loading: boolean = false;

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  chartConstructor = "mapChart";
  usPrefix = 'us-';

  constructor(
    private shortDisplayValuePipe: ShortDisplayValuePipe
  ){}

  private getChartData(): [string, number | null][] {
    return (this.data?.performance ?? [])
      .map((item) => {
        const rawValue = item[this.metric?.key as keyof typeof item];
        const value = typeof rawValue === 'number' ? rawValue : null;
        const code = this.type === PerformanceMapType.US ? `${this.usPrefix}${item.geoCode}` : item.geoCode;
        return [code, value] as [string, number | null];
      })
      .filter(([code, value]) => typeof code === 'string' && code && value !== undefined);
  }

  private getImpressionsData(): Record<string, number> {
    const impressionsData: Record<string, number> = {};
    (this.data?.performance ?? []).forEach((item) => {
      const code = this.type === PerformanceMapType.US ? `${this.usPrefix}${item.geoCode}` : item.geoCode;
      impressionsData[code] = item['impressions'] ?? 0;
    });
    return impressionsData;
  }

  private getTooltipHtml(country: string, metric?: SelectItem, value?: string, impressions?: string): string {
    let content: string;
    if (!value || !impressions) {
      content = `<div>No data found for ${country}</div>`;
    } else {
      content = `
        <div class="tooltip-country" style="font-weight: 700;">${country}</div>
        <div class="tooltip-metric" style="font-weight: 400;">
          <span class="tooltip-label">${metric?.label}:</span>
          <span class="tooltip-value" style="font-weight: 600;">${value}</span>
        </div>
      `;
      if (metric?.key !== PerformanceComparisonMetric.IMPRESSIONS) {
        content += `
          <div class="tooltip-divider" style="border-top: 1px solid #DEE0E4"></div>
          <div class="tooltip-metric" style="font-weight: 400;">
            <span class="tooltip-label">Impressions:</span>
            <span class="tooltip-value" style="font-weight: 600;">${impressions}</span>
          </div>
        `;
      }
    }
    const html = `
      <div class="tooltip-content"
        style="
          display: flex;
          flex-direction: column;
          padding: 12px;
          gap: 8px;
          color: #34363A;
          line-height: 18px;
          font-style: normal;
          font-family: Poppins;
          font-size: 12px;
          background: #FFFFFF;
          border-radius: 4px;
          box-shadow: 0px 0px 8px 2px rgba(0, 0, 0, 0.15);
        ">
      ${content}
      </div>
    `;
    return html;
  }

  private getFormattedMetric(value: number, fixed: boolean = true): string {
    switch (this.metric?.key) {
      case PerformanceComparisonMetric.ATTENTION:
        return `${value}%`;
      case PerformanceComparisonMetric.DWELL_TIME:
        return `${value}s`;
      case PerformanceComparisonMetric.REFRESH_RATE:
        return `${value}s`;
      case PerformanceComparisonMetric.CPM:
        return fixed ? `$${value.toFixed(2)}` : `$${value}`;
      case PerformanceComparisonMetric.IMPRESSIONS:
        return `${this.shortDisplayValuePipe.transform(value, fixed)}`;
      case PerformanceComparisonMetric.AD_CLUTTER:
        return `${value.toFixed(1)}`;
      case PerformanceComparisonMetric.SPEND:
        return `${this.shortDisplayValuePipe.transform(value, fixed, 100, '$')}`;
      default:
        return `${value}`;
    }
  }

  private getMinMaxMetricValue(): { min: number, max: number } {
    const values: number[] = (this.data?.performance ?? [])
      .map(item => item[this.metric?.key as keyof typeof item])
      .filter((v): v is number => typeof v === 'number');

    if (values.length === 0) {
      return {min: 0, max: 0};
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    return {min, max};
  }

  ngOnChanges() {
    if (!this.data?.performance?.length) return;

    const globalStyle = {fontFamily: 'Poppins', fontSize: '12px'};
    const map = this.type === PerformanceMapType.US ? usaMap : worldMap;
    const currentMetric = this.metric;
    const chartData = this.getChartData();
    const realData = chartData.filter(([_, value]) => value !== null);
    const nullData = chartData.filter(([_, value]) => value === null).map(([code]) => [code, 0]);
    const realCodes = new Set(realData.map(([code]) => code));
    const nullCodes = new Set(nullData.map(([code]) => code));
    map.features.forEach((f: any) => {
      const code = f.properties['hc-key'];
      if (!realCodes.has(code) && !nullCodes.has(code)) {
        nullData.push([code, 0]);
      }
    });
    const impressionsData = this.getImpressionsData();
    const getTooltipHtml = this.getTooltipHtml.bind(this);
    const getFormattedMetric = this.getFormattedMetric.bind(this);
    const shortDisplayValuePipe = this.shortDisplayValuePipe.transform.bind(this.shortDisplayValuePipe);
    const {min: minMetricValue, max: maxMetricValue} = this.getMinMaxMetricValue();

    this.chartOptions = {
      chart: {
        map: map,
        animation: false,
        height: 502,
        width: 1132,
        marginTop: 0,
        events: {
          load: function () {
            const chart = this;
            const legendBox = chart.legend.group.getBBox();
            chart.renderer.rect(legendBox.x + legendBox.width + 39, legendBox.y + 470, 40, 12, 3).attr({
                fill: '#BDC2CA',
              }).add();
            chart.renderer.text('No Data', legendBox.x + legendBox.width + 35, legendBox.y + 502).css({
                color: '#333333',
                fontSize: '12px',
                fontFamily: 'Poppins'
              }).add();
          }
        }
      },
      title: {
        text: undefined
      },
      credits: {
        enabled: false
      },
      mapNavigation: {
        enabled: false
      },
      legend: {
        layout: 'horizontal',
        align: 'left',
        verticalAlign: 'bottom',
        symbolWidth: 320,
        itemStyle: globalStyle,
        y: 24
      },
      series: [
        {
          type: "map",
          name: currentMetric?.label,
          data: realData,
          allAreas: false,
          borderColor: '#FFFFFF',
          borderWidth: 1,
          states: {
            hover: { enabled: false },
            inactive: { enabled: false }
          },
          dataLabels: { enabled: false },
        },
        {
          type: "map",
          name: 'No data',
          data: nullData,
          color: '#BDC2CA',
          colorAxis: false,
          showInLegend: false,
          joinBy: 'hc-key',
          allAreas: false,
          borderColor: '#FFFFFF',
          borderWidth: 1,
          states: {
            hover: { enabled: false },
            inactive: { enabled: false }
          },
          dataLabels: { enabled: false },
          enableMouseTracking: true,
        } as any
      ],
      colorAxis: {
        min: minMetricValue,
        max: maxMetricValue,
        tickAmount: 6,
        allowDecimals: false,
        stops: [
          [0, '#ABD6D4'],
          [0.2, '#81C2BE'],
          [0.4, '#58AEA9'],
          [0.6, '#04857E'],
          [0.8, '#035954'],
          [1, '#02433F']
        ],
        labels: {
          style: globalStyle,
          formatter: function (this: any) {
            return getFormattedMetric(this.value, false);
          }
        },
      },
      tooltip: {
        useHTML: true,
        shadow: false,
        backgroundColor: 'transparent',
        formatter: function (this: any) {
          const country = this.point?.name;
          const geoCode = this.point?.['hc-key'];
          if (this.series?.name === 'No data') {
            return getTooltipHtml(country, currentMetric, undefined, undefined);
          }
          const value = getFormattedMetric(this.point?.value);
          const impressions = shortDisplayValuePipe(impressionsData[geoCode]);
          return getTooltipHtml(country, currentMetric, value, impressions);
        }
      }
    }
  }


}
